/**
 * Wheelchair assistance on a trip's passenger card (founder ask, 2026-10-05).
 *
 * Shows a traveler's four airline answers to the customer and to admins, on
 * the same Trips modal. The trip's owner can change them at any time while
 * the trip is open (the server emails support@ when they do); an admin can
 * record them for the customer, e.g. from an emailed reply. Every other
 * viewer, and every viewer of a closed trip, reads them.
 *
 * Rendered only for a traveler whose special request mentions a wheelchair,
 * or who already has answers (needsWheelchairQuestions).
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { Accessibility, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import WheelchairQuestions from '../passenger/WheelchairQuestions';
import { saveWheelchairAnswers } from '@/api/wheelchairAssistance';
import {
  diffWheelchairAnswers,
  normalizeWheelchairAnswers,
  wheelchairComplete,
} from '../../../supabase/functions/_shared/wheelchair-assistance.js';

function lastUpdatedLine(record, viewerIsOwner) {
  if (!record?.updated_at) return null;
  let when = '';
  try { when = format(new Date(record.updated_at), 'MMM d, h:mm a'); } catch { /* keep the who */ }
  const who = record.updated_by === 'admin'
    ? (viewerIsOwner ? 'by the Defera team' : 'by an admin')
    : (viewerIsOwner ? 'by you' : 'by the customer');
  return `Last updated ${when ? `${when} ` : ''}${who}`;
}

export default function WheelchairAssistanceCard({ passenger, canEdit = false, viewerIsOwner = false, closed = false, onSaved }) {
  const record = passenger?.wheelchairAssistance || passenger?.wheelchair_assistance || null;
  const [draft, setDraft] = useState(() => normalizeWheelchairAnswers(record));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // A save (here or elsewhere) replaces the record: start over from it.
  useEffect(() => { setDraft(normalizeWheelchairAnswers(record)); }, [record]);

  const editable = canEdit && !closed;
  const dirty = diffWheelchairAnswers(record, draft).length > 0;
  const complete = wheelchairComplete(record);
  const updated = lastUpdatedLine(record, viewerIsOwner);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = await saveWheelchairAnswers(passenger.id, draft);
      onSaved?.(passenger.id, body.wheelchair_assistance ?? record);
    } catch (err) {
      setError(err?.message || 'Could not save the answers.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 rounded-2xl border border-[hsl(228,35%,58%)]/15 bg-[hsl(228,40%,38%)]/[0.06] px-3.5 py-3.5 space-y-4">
      <div className="flex items-start gap-2.5">
        <Accessibility className="w-4 h-4 mt-0.5 text-[hsl(228,35%,58%)] flex-shrink-0" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[13.5px] font-medium text-white leading-snug">Wheelchair assistance</p>
            <span className={cn(
              'text-[11px] font-normal rounded-full px-2 py-px border',
              complete
                ? 'text-emerald-200/90 border-emerald-400/25 bg-emerald-950/40'
                : 'text-amber-200/90 border-amber-400/25 bg-amber-950/40',
            )}>
              {complete ? 'Answered' : 'Needs answers'}
            </span>
          </div>
          <p className="mt-1 text-[12.5px] leading-relaxed font-normal text-white/60">
            {closed
              ? 'This trip is closed, so these answers can no longer change.'
              : editable && viewerIsOwner
                ? 'The airline needs these answers to arrange assistance. You can change them anytime.'
                : 'The airline needs these answers to arrange assistance.'}
          </p>
        </div>
      </div>

      <WheelchairQuestions
        value={draft}
        onChange={setDraft}
        disabled={!editable || saving}
        idPrefix={`trip-pax-${passenger?.id}-wheelchair`}
      />

      {(updated || (editable && dirty) || error) && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[11.5px] font-normal text-white/45">{updated}</p>
          {editable && dirty && (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={saving}
                onClick={() => { setDraft(normalizeWheelchairAnswers(record)); setError(null); }}
                className="h-8 rounded-full px-3 text-[12px] font-normal text-white/70 hover:text-white hover:bg-white/[0.06]"
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={saving}
                onClick={save}
                className="h-8 rounded-full px-4 text-[12px] font-medium bg-brand-600 hover:bg-brand-500 text-white"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-label="Saving" /> : 'Save answers'}
              </Button>
            </div>
          )}
          {error && <p className="w-full text-[12px] font-normal text-rose-300" role="alert">{error}</p>}
        </div>
      )}
    </div>
  );
}

WheelchairAssistanceCard.propTypes = {
  passenger: PropTypes.object.isRequired,
  canEdit: PropTypes.bool,
  viewerIsOwner: PropTypes.bool,
  closed: PropTypes.bool,
  onSaved: PropTypes.func,
};
